/**
 * Host-level navigation registry.
 *
 * `AppNav.vue` renders every entry pushed here. Features contribute nav
 * links during their `install*` entrypoint via `addNavLinks(...)`; the
 * host shell sorts the entries by `order` ascending before rendering.
 *
 * Phase 7 makes this the single source of truth: `AppNav.vue` no longer
 * hard-codes which surfaces exist or who's allowed to see them — each
 * feature owns its visibility rule and pushes a `NavLinkDescriptor` with
 * a `visible: ComputedRef<boolean>` that re-evaluates on every relevant
 * state change (sign-in, guild selection, raid snapshot state, etc.).
 *
 * Labels and profile-popover labels are stored as `ComputedRef<string>`
 * so they react immediately to locale changes from the host locale
 * port. Freezing the English label at install time would leave every
 * Russian-language nav entry stuck on the English string forever; the
 * `ComputedRef` shape makes `AppNav.vue` re-render whenever the
 * effective locale (`localePort.locale`) flips.
 */
import type { ComputedRef } from 'vue'

import type { IconName } from '@/shared/ui/BaseIcon.vue'

export interface NavLinkDescriptor {
  /** The Vue Router route name this entry navigates to. */
  readonly routeName: string
  /**
   * Optional, reactive route params to pass to the resolved route. Required
   * when the target route declares params (e.g. the planner's
   * `/:guildId/plan/:raidId`); the descriptor carries the live values via a
   * `ComputedRef` so the host `RouterLink` re-resolves on every change.
   * When `undefined` or its value is `undefined`, the host renders a
   * `{ name: … }` location without params.
   */
  readonly params?: ComputedRef<Readonly<Record<string, string>> | undefined>
  /**
   * Reactive human-readable label shown inside the primary nav strip.
   * Stored as a `ComputedRef<string>` so the rendered text follows
   * locale changes (the descriptor set itself is immutable for the
   * app's lifetime, but the value it surfaces is recomputed on every
   * effective-locale flip).
   */
  readonly label: ComputedRef<string>
  /** Optional icon (matches `IconName` from `@/shared/ui/BaseIcon.vue`). */
  readonly icon?: IconName
  /** Reactive visibility — the descriptor is filtered out while `false`. */
  readonly visible: ComputedRef<boolean>
  /** Stable rendering order: lower = earlier. */
  readonly order: number
  /**
   * Optional profile-popover channel. When set, the entry is also surfaced
   * by `getProfilePopoverLinks()` so the host `ProfilePopover.vue` can render
   * it inside the header profile popover (e.g. cross-server build management
   * and officer-only surfaces that don't belong in the primary nav strip).
   * Keep narrow: a reactive label and optional icon — anything more
   * stateful belongs in a feature-owned component, not in the descriptor
   * record.
   */
  readonly profilePopover?: {
    readonly label: ComputedRef<string>
    readonly icon?: IconName
  }
}

/**
 * A `NavLinkDescriptor` narrowed to entries that opt into the profile-popover
 * channel via `profilePopover`. Returned by `getProfilePopoverLinks()`.
 */
export type ProfilePopoverLink = Omit<NavLinkDescriptor, 'profilePopover'> & {
  readonly profilePopover: {
    readonly label: ComputedRef<string>
    readonly icon?: IconName
  }
}

const links: NavLinkDescriptor[] = []

/**
 * Append entries to the registry. Called from each feature's
 * `install*()` entrypoint. Order is preserved on push; sort runs at
 * read-time via {@link getNavLinks}.
 */
export function addNavLinks(entries: ReadonlyArray<NavLinkDescriptor>): void {
  for (const entry of entries) {
    links.push(entry)
  }
}

/**
 * Snapshot the registry sorted by `order` ascending. The returned array
 * is a fresh shallow copy so callers can filter / partition without
 * mutating the registry.
 */
export function getNavLinks(): ReadonlyArray<NavLinkDescriptor> {
  return [...links].sort((a, b) => a.order - b.order)
}

/**
 * Snapshot the primary-nav slice: descriptors without a `profilePopover`
 * channel, sorted by `order` ascending. The primary nav strip and profile
 * popover are disjoint slices of the same registry, and the returned array
 * is a fresh shallow copy.
 */
export function getPrimaryNavLinks(): ReadonlyArray<NavLinkDescriptor> {
  return [...links]
    .filter((entry) => entry.profilePopover === undefined)
    .sort((a, b) => a.order - b.order)
}

/**
 * Snapshot the subset of registered descriptors that opt into the
 * profile-popover channel (`profilePopover` field present), sorted by
 * `order` ascending. Returned array is a fresh shallow copy.
 */
export function getProfilePopoverLinks(): ReadonlyArray<ProfilePopoverLink> {
  return links
    .filter(
      (entry): entry is ProfilePopoverLink => entry.profilePopover !== undefined,
    )
    .sort((a, b) => a.order - b.order)
}

/**
 * Clear the registry. Test-only escape hatch — production callers should
 * not need to wipe the list between boots because `main.ts` builds a
 * fresh app per page load.
 */
export function resetNavLinks(): void {
  links.length = 0
}